import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Menu from '../menu/Menu';
import Tabs from 'react-bootstrap/Tabs';
import Tab from 'react-bootstrap/Tab';
import RTButton from '../common/RTButton';
import RTSpinner from '../common/RTSpinner';
import AddPayment from '../payments/AddPayment';
import AddAdjustment from '../payments/AddAdjustment';
import { apiGet } from '../../api/apiClient';
import { toastError } from '../common/toast';
import PGGeneralTab from './PGGeneralTab';
import PGInvoicesTab from './PGInvoicesTab';
import PGPaymentsTab from './PGPaymentsTab';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../common/rtCommon.css';
import './PGDetailView.css';

const PGDetailView = () => {
  const { pgId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(location.state?.activeTab || 'general');
  const [pg, setPg] = useState(null);
  const [allocation, setAllocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allocationLoading, setAllocationLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showAddPayment, setShowAddPayment] = useState(false);
  const [selectedInvoiceForPayment, setSelectedInvoiceForPayment] = useState(null);
  const [showAddAdjustment, setShowAddAdjustment] = useState(false);
  const [selectedInvoiceForAdjustment, setSelectedInvoiceForAdjustment] = useState(null);

  useEffect(() => {
    const abortController = new AbortController();
    
    const loadData = async () => {
      if (pgId) {
        try {
          await fetchPGData();
          if (!abortController.signal.aborted) {
            await fetchAllocation();
          }
        } catch (error) {
          if (error.name !== 'AbortError') {
            console.error('Error loading PG data:', error);
          }
        }
      }
    };
    
    loadData();
    
    // Cleanup function to abort requests if component unmounts
    return () => {
      abortController.abort();
    };
  }, [pgId]);

  const fetchPGData = async () => {
    try {
      const response = await apiGet(`/pgdetails/${pgId}`);
      setPg(response.data);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error fetching PG data:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchAllocation = async () => {
    try {
      setAllocationLoading(true);
      const response = await apiGet(`/allocation/by-pg/${pgId}`);
      setAllocation(response.data);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error fetching allocation:', error);
        if (error.response?.status === 404) {
          setAllocation(null);
        }
      }
    } finally {
      setAllocationLoading(false);
    }
  };

  const handleTabSelect = (tabKey) => {
    setActiveTab(tabKey);
  };

  const handleEdit = () => {
    setIsEditMode(true);
    setActiveTab('general'); // Switch to general tab when editing
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
  };

  const handleSaveEdit = (updatedPGData) => {
    setPg(updatedPGData);
    setIsEditMode(false);
  };

  const handleAllocateBed = (pgData) => {
    // Navigate to AllocateBed with state containing the selected PG info
    navigate('/bedallocation/allocate-bed', { 
      state: { 
        selectedPG: {
          ...pgData,
          pg_id: pgId // Ensure pg_id is included from URL params
        }
      }
    });
  };

  const handleViewAllocationDetails = () => {
    if (allocation && allocation.allocation_id) {
      navigate(`/bedallocation/allocation/${allocation.allocation_id}`);
    } else {
      toastError('No allocation details available for this PG.');
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleShowAddPayment = (invoiceData, pgData) => {
    setSelectedInvoiceForPayment({
      invoice: invoiceData,
      pg: pgData
    });
    setShowAddPayment(true);
  };

  const handleCloseAddPayment = () => {
    setShowAddPayment(false);
    setSelectedInvoiceForPayment(null);
    // Force refresh the current tab by remounting it
    const currentTab = activeTab;
    setActiveTab('general');
    setTimeout(() => setActiveTab(currentTab), 0);
  };

  const handleShowAddAdjustment = (invoiceData, pgData) => {
    setSelectedInvoiceForAdjustment({
      invoice: invoiceData,
      pg: pgData
    });
    setShowAddAdjustment(true);
  };

  const handleCloseAddAdjustment = (dataChanged) => {
    setShowAddAdjustment(false);
    setSelectedInvoiceForAdjustment(null);
    // If data changed, force refresh invoices by remounting the tab
    if (dataChanged) {
      setActiveTab('general');
      setTimeout(() => setActiveTab('invoices'), 0);
    }
  };

  // If AddAdjustment is shown, render only the AddAdjustment component
  if (showAddAdjustment && selectedInvoiceForAdjustment) {
    return (
      <AddAdjustment
        onClose={handleCloseAddAdjustment}
        invoiceData={selectedInvoiceForAdjustment.invoice}
        pgData={selectedInvoiceForAdjustment.pg}
      />
    );
  }

  // If AddPayment is shown, render only the AddPayment component
  if (showAddPayment && selectedInvoiceForPayment) {
    return (
      <AddPayment
        onClose={handleCloseAddPayment}
        preSelectedPG={selectedInvoiceForPayment.pg}
        preSelectedInvoiceId={selectedInvoiceForPayment.invoice?.invoice_id || null}
      />
    );
  }

  if (loading) {
    return (
      <div className="rt-layout">
        <Menu className="rt-menu" />
        <div className="rt-main-content">
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <RTSpinner message="Loading paying guest details..." />
          </div>
        </div>
      </div>
    );
  }

  if (!pg) {
    return (
      <div className="rt-layout">
        <Menu className="rt-menu" />
        <div className="rt-main-content">
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <p>No data found for this paying guest.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rt-layout">
      <Menu className="rt-menu" />
      <div className="rt-main-content">
        <div className="rt-title-row">
          <h2 className="rt-title">Paying Guest Details - {pg?.name}</h2>
          <div className="rt-actions">
            <RTButton variant='secondary' onClick={handleBack}>
              &#8592; Back
            </RTButton>
            {!isEditMode ? (
              <>
                <RTButton variant='secondary' onClick={handleEdit}>
                  Edit
                </RTButton>
                {pg?.status === 'Active' ? (
                  <RTButton variant='primary' onClick={handleViewAllocationDetails}>
                    View Allocation Details
                  </RTButton>
                ) : (
                  <RTButton variant='primary' onClick={() => handleAllocateBed(pg)}>
                    Allocate Bed
                  </RTButton>
                )}
              </>
            ) : null}
          </div>
        </div>

        <div className="rt-main-section">
          <Tabs
            activeKey={activeTab}
            onSelect={handleTabSelect}
            id="pg-details-tabs"
            className="rt-fullwidth-tabs"
            justify
          >
            {/* General Tab */}
            <Tab eventKey="general" title="General" style={{ minHeight: '60%', border: '0.5px solid #e0e0e0' }}>
              <PGGeneralTab 
                pgData={pg}
                allocation={allocation}
                isLoading={loading}
                allocationLoading={allocationLoading}
                isEditMode={isEditMode}
                onEditToggle={handleCancelEdit}
                onRefresh={fetchPGData}
              />
            </Tab>

            {/* Invoices Tab */}
            <Tab eventKey="invoices" title="Invoices" style={{ border: '.5px solid #e0e0e0' }}>
              <PGInvoicesTab pgId={pgId} onShowAddPayment={handleShowAddPayment} onShowAddAdjustment={handleShowAddAdjustment} />
            </Tab>

            {/* Payments Tab */}
            <Tab eventKey="payments" title="Payments" style={{ border: '.5px solid #e0e0e0' }}>
              <PGPaymentsTab pgId={pgId} onShowAddPayment={handleShowAddPayment} />
            </Tab>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default PGDetailView;